import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Switch } from "@/components/ui/baseUi/switch"
import { Text } from "@/components/ui/baseUi/text"
import { NewChatGroupNameForm } from "@/modules/chat/components/newChatGroupNameForm"
import { ChannelType } from "@/modules/chat/gateway/channelTypes"
import { cn } from "@/modules/lib/utils"
import { ChatAvatar } from "@modules/chat/components/chatAvatar"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { CHANNEL_LIST_SHOWN_WINDOW_WIDTH, DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH } from "@modules/chat/utils/chatConstants"
import { getChannelCreatorId } from "@modules/chat/utils/getChannelCreatorId"
import { word } from "@modules/core/utils/i18n"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { PencilIcon, UsersIcon, Volume1Icon, VolumeOffIcon, XIcon } from "lucide-react"
import { useEffect, useState } from "react"
import type { Event } from "stream-chat"
import { getDisplayTitle, useChannelPreviewInfo, useChannelStateContext, useChatContext } from "stream-chat-react"
import { useChatClient } from "@/modules/chat/stores/useChatStore"

type MemberType = {
  id: string
  name: string
  online: boolean
  avatar?: string
  banned: boolean
  role?: string
}

export const ChannelDetailsOverlay: React.FC = () => {
  const chatClient = useChatClient()
  const { setActiveChannel } = useChatContext()
  const { channel, channelCapabilities } = useChannelStateContext()
  const { displayImage } = useChannelPreviewInfo({ channel })
  const {
    displayChatDetailsOverlay,
    toggleDisplayChatDetailsOverlay,
    updateIsAddingMembersToChannel,
    updateIsCreatingGroup,
  } = useChatPageContext()
  const { width } = useWindowSize()
  const confirmPopup = useConfirmPopup()

  const [muted, setMuted] = useState<boolean>(channel.muteStatus().muted)
  const [channelUsers, setChannelUsers] = useState<Array<MemberType>>([])
  const [displayGroupNameInput, setDisplayGroupNameInput] = useState<boolean>(false)

  useEffect(() => {
    // workaround to deal with the real time changes issue in stream-chat contexts
    const updateUsers = (action?: "ban" | "unban" | "remove", userId?: string) => {
      const isUpdatedUserBanned = (defaultValue: boolean) => {
        switch (action) {
          case "ban":
            return true
          case "remove":
            return true
          case "unban":
            return false
          default:
            return defaultValue
        }
      }

      setChannelUsers(
        Object.values(channel.state.members).map((user) => {
          const is_banned = user.user_id === userId ? isUpdatedUserBanned(user.banned || false) : user.banned || false

          return {
            id: user.user_id!,
            name: user.user!.name ?? user.user_id!,
            avatar: user.user!.image,
            online: !!user.user!.online,
            banned: is_banned,
            role: user.role,
          }
        }),
      )
    }

    updateUsers()

    const handleBanUser = (e: Event) => {
      if (!e.user) return
      updateUsers("ban", e.user.id)
    }

    const handleUnbanUser = (e: Event) => {
      if (!e.user) return
      updateUsers("unban", e.user.id)
    }

    const handleRemoveUser = (e: Event) => {
      if (!e.user) return
      updateUsers("remove", e.user.id)
    }

    if (chatClient) {
      chatClient.on("user.banned", handleBanUser)
      chatClient.on("user.unbanned", handleUnbanUser)
      chatClient.on("member.removed", handleRemoveUser)
    }

    return () => {
      if (chatClient) {
        chatClient.off("user.banned", handleBanUser)
        chatClient.off("user.unbanned", handleUnbanUser)
        chatClient.off("member.removed", handleRemoveUser)
      }
    }
  }, [chatClient, channel])

  if (!channel || !chatClient) return null

  const isOneOnOneConversation =
    channel && Object.values(channel.state.members).length === 2 && channel.id?.indexOf("!members-") === 0
  const channelCreatorId = getChannelCreatorId(channel)
  const isProgramGroup = channel.type === ChannelType.PROGRAM
  const isBoxGroup = channel.type === ChannelType.BOX
  const isCurrentUserGroupCreator = channelCreatorId === chatClient.user?.id
  const isProgramGroupMember = isProgramGroup && !isCurrentUserGroupCreator
  const isBoxGroupMember = channel.type === ChannelType.BOX && !isCurrentUserGroupCreator
  const canUserEditChannelName = !isOneOnOneConversation && !isProgramGroup && !isBoxGroup && isCurrentUserGroupCreator
  const canUserSeeMemberList = isOneOnOneConversation || (!isProgramGroupMember && !isBoxGroupMember)
  const canUserEditMemberList = !isOneOnOneConversation && !isProgramGroupMember && !isBoxGroupMember

  const toggleMuted = async () => {
    if (muted) {
      await channel.unmute()
    } else {
      await channel.mute()
    }

    setMuted((previousState) => !previousState)
  }

  const confirmAction = (action: "leave" | "kick" | "delete" | "ban", member?: MemberType): void => {
    const getConfirmPopupContent = () => {
      switch (action) {
        case "delete":
          return {
            title: word("chat.delete_conversation.title"),
            message: word("chat.delete_conversation.message"),
          }
        case "leave":
          return {
            title: word("chat.leave_group.title"),
            message: word("chat.leave_group.message"),
          }
        case "kick":
          return {
            title: word("chat.kick_from_group.title"),
            message: word("chat.kick_from_group.message", { username: member?.name }),
          }
        case "ban":
          return {
            title: word("chat.mute_user.title"),
            message: word("chat.mute_user.message"),
          }
        default:
          return {
            title: word("global.error.label"),
            message: word("global.error.default"),
          }
      }
    }

    const { title, message } = getConfirmPopupContent()

    confirmPopup.show({
      title,
      message,
      accept: async () => {
        if (action === "delete") await deleteChannel()
        else if (action === "leave") await leaveGroup()
        else if (action === "kick" && member) await kickMember(member)
        else if (action === "ban" && member) await banMember(member)
        if (action === "delete" || action === "leave") selectFirstChannel()
      },
    })
  }

  const leaveGroup = async () => {
    if (chatClient.user?.id) {
      await channel.removeMembers([chatClient.user.id])
    }
  }

  const kickMember = async (member: MemberType) => {
    await channel.removeMembers([member.id])
  }

  const banMember = async (member: MemberType) => {
    await channel.banUser(member.id, {
      banned_by_id: chatClient.user?.id,
    })
  }

  const unbanMember = async (member: MemberType) => {
    await channel.unbanUser(member.id)
  }

  const deleteChannel = async () => {
    await channel.hide(undefined, true)
  }

  const selectFirstChannel = () => {
    if (chatClient.activeChannels)
      setActiveChannel(chatClient.activeChannels[Object.keys(chatClient.activeChannels)[0]!])
  }

  const updateGroupName = async (newGroupName: string) => {
    await channel.updatePartial({ set: { team: newGroupName } })
    setDisplayGroupNameInput(false)
  }

  const addMemberToChannel = () => {
    updateIsCreatingGroup(false)
    updateIsAddingMembersToChannel(channel)
  }

  return (
    <div
      className={cn(
        "min-w-50 overflow-hidden rounded-tr-xl border border-neutral-200 transition-all duration-150 ease-linear lg:min-w-62.5",
        { "ml-3": width > CHANNEL_LIST_SHOWN_WINDOW_WIDTH },
        { "max-w-full": displayChatDetailsOverlay },
        { "max-w-0 min-w-0": !displayChatDetailsOverlay },
      )}
    >
      <div className="relative flex h-full w-50 flex-col items-center justify-start overflow-y-auto bg-surface-background px-1 py-12.5 lg:w-62.5 lg:px-4">
        {width <= DETAILS_OVERLAY_SHOWN_WINDOW_WIDTH && (
          <Button
            size="iconMd"
            variant="outline"
            onClick={toggleDisplayChatDetailsOverlay}
            className="absolute top-0 right-0 mt-2 mr-2"
          >
            <XIcon className="size-4" />
          </Button>
        )}

        <ChatAvatar image={displayImage} name={channel.data?.team} profileAvatarClassName="size-15" />

        <div className="mt-2.5 flex flex-row items-center justify-center">
          <Text fontStyle="LL">{getDisplayTitle(channel, chatClient.user)}</Text>
          {canUserEditChannelName && (
            <Button
              size="iconMd"
              variant="ghost"
              onClick={() => setDisplayGroupNameInput(true)}
              className="ml-2 hover:bg-neutral-100"
            >
              <PencilIcon className="size-4" />
            </Button>
          )}
        </div>

        {canUserSeeMemberList && (
          <>
            <div className="mt-7.5 h-0.5 w-[50%] border-t border-neutral-200" />

            <div className="w-full pt-5">
              {channelUsers.map((member) => {
                const isMemberGroupCreator = channelCreatorId === member.id
                return (
                  <div key={member.id} className="my-2.5 flex h-7 flex-row items-center justify-between gap-1.5">
                    <div className="flex flex-row items-center gap-1">
                      <ChatAvatar
                        image={member.avatar}
                        name={member.name}
                        isOnline={member.online}
                        profileAvatarClassName="size-8"
                      />

                      <Text fontStyle="BM" className="line-clamp-2">
                        {member.name}
                      </Text>
                    </div>

                    <div className="flex flex-row pr-2">
                      {isMemberGroupCreator && <Text fontStyle="BS">{word("chat.owner")}</Text>}
                      {channelCapabilities["ban-channel-members"] && !isMemberGroupCreator && (
                        <Button
                          size="iconMd"
                          variant="ghost"
                          onClick={() => {
                            if (member.banned) unbanMember(member)
                            else confirmAction("ban", member)
                          }}
                          className="h-8 w-8 cursor-pointer p-1 hover:bg-neutral-100"
                        >
                          {member.banned ? <VolumeOffIcon className="size-4" /> : <Volume1Icon className="size-4" />}
                        </Button>
                      )}
                      {channelCapabilities["update-channel"] &&
                        channel.type === ChannelType.PRIVATE &&
                        !isMemberGroupCreator && (
                          <Button
                            size="iconMd"
                            variant="ghost"
                            onClick={() => confirmAction("kick", member)}
                            className="h-8 w-8 cursor-pointer p-1 hover:bg-neutral-100"
                          >
                            <XIcon className="size-4" />
                          </Button>
                        )}
                    </div>
                  </div>
                )
              })}

              {channel.type === ChannelType.PRIVATE && canUserEditMemberList && (
                <Button
                  size="iconMd"
                  variant="ghost"
                  onClick={addMemberToChannel}
                  className="w-full cursor-pointer p-1 text-neutral-400 hover:bg-neutral-100"
                >
                  <UsersIcon className="size-4" color="currentColor" />
                  {word("chat.add_group_members")}
                </Button>
              )}
            </div>
          </>
        )}
        <div className="mt-auto h-0.5 w-[50%] border-t border-neutral-200" />
        <div className="flex w-full flex-col items-center pt-5">
          <div
            onClick={() => {
              toggleMuted()
            }}
            className="inline-flex size-9 h-12.5 w-full shrink-0 cursor-pointer items-center justify-center gap-4 rounded-md p-1 text-sm font-medium whitespace-nowrap text-neutral-500 transition-all outline-none hover:bg-neutral-50"
          >
            {word("chat.mute_group.button")}
            <Switch checked={muted} className="cursor-pointer" />
          </div>

          {isOneOnOneConversation && !channel.data?.hidden && (
            <Button
              size="iconMd"
              variant="ghost"
              onClick={() => confirmAction("delete")}
              className="h-12.5 w-full cursor-pointer gap-4 rounded-md p-1 text-neutral-500 hover:bg-neutral-50 hover:ring-0"
            >
              {word("chat.delete_conversation.button")}
            </Button>
          )}

          {!isOneOnOneConversation && !isProgramGroup && !isBoxGroup && (
            <Button
              size="iconMd"
              variant="ghost"
              onClick={() => confirmAction("leave")}
              className="h-12.5 w-full cursor-pointer gap-4 rounded-md p-1 text-neutral-500 hover:bg-neutral-50 hover:ring-0"
            >
              {word("chat.leave_group.button")}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={displayGroupNameInput} onOpenChange={() => setDisplayGroupNameInput(false)}>
        <DialogContent className="w-125">
          <DialogHeader>
            <DialogTitle>
              <Text fontStyle="TL" className="flex w-full pb-4 pl-4">
                {word("chat.update_group_name")}
              </Text>
            </DialogTitle>
          </DialogHeader>
          <NewChatGroupNameForm
            onValidate={(newGroupName) => {
              updateGroupName(newGroupName)
            }}
            onCancel={() => {
              setDisplayGroupNameInput(false)
            }}
            defaultGroupName={channel.data?.team}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
